import { noop } from 'lodash';
import { useMemo } from 'react';

import { VisitMedicalImaging, VisitMedicalImagingQuery } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering, Translate } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   StatusBadge,
   StyledFlex,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';

import { medicalImagingTranslationKeys } from '../../../../translations';

import { MedicalImagingDetailsModalLoader } from './MedicalImagingDetailsModal.loader';
import { MedicalImagingDetailsModalOpener } from './MedicalImagingDetailsModal.opener';

const DEFAULT_ORDERINGS: Ordering<VisitMedicalImaging>[] = [
   {
      descending: true,
      property: 'examinationDate',
   },
];

export const useMedicalImagingDataConfiguration = (
   visitId: number
): LazyLoadedDataConfigurationByCqrsQuery<VisitMedicalImaging> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<VisitMedicalImaging>(() => {
      const query = new VisitMedicalImagingQuery();
      query.visitId = visitId;
      return query;
   }, DEFAULT_ORDERINGS);
};

export const useMedicalImagingTableColumns = (): TableColumnConfiguration<VisitMedicalImaging>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         icd9Code: '72px',
         icd9Name: '100%',
         examinationDate: '106px',
         options: '36px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<VisitMedicalImaging> = (props) =>
         props.bean.removed ? colorsMatrix.red.lighter : undefined;

      return [
         {
            name: 'icd9Code',
            label: medicalImagingTranslationKeys.table.icd9Code,
            accessor: noop,
            width: columnsWidth.icd9Code,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalImaging>) => (
               <span>{bean.icd9.getCode().asString()}</span>
            ),
         },
         {
            name: 'icd9Name',
            label: medicalImagingTranslationKeys.table.icd9Name,
            accessor: noop,
            width: columnsWidth.icd9Name,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalImaging>) => (
               <span>{bean.icd9.getName().asString()}</span>
            ),
         },
         {
            name: 'examinationDate',
            label: medicalImagingTranslationKeys.table.examinationDate,
            accessor: noop,
            width: columnsWidth.examinationDate,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalImaging>) => (
               <StyledFlex gap={2} direction={'column'}>
                  <Text testId={'examinationDate'}>{dateFormatter(bean.examinationDate)}</Text>
                  {bean.removed ? (
                     <StatusBadge testId={'medicalImagingStatus'} type={'unapproved'} compact>
                        <Translate>{medicalImagingTranslationKeys.table.removed}</Translate>
                     </StatusBadge>
                  ) : null}
               </StyledFlex>
            ),
         },
         {
            name: 'options',
            accessor: noop,
            width: columnsWidth.options,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<VisitMedicalImaging>) => (
               <MedicalImagingDetailsModalLoader visitMedicalImaging={bean}>
                  {({ medicalImaging }) => <MedicalImagingDetailsModalOpener medicalImaging={medicalImaging} />}
               </MedicalImagingDetailsModalLoader>
            ),
         },
      ];
   }, []);
};
